import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import AuthLayout from "components/AuthLayout/AuthLayout";
import { authPageMeta } from "data/authPages.enum";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { verifyOtp, resendVerificationCode } from "store/slices/authSlice";
import { RootState } from "store/Store";
import { useAppDispatch } from "store/hooks/hooks";
import { ClipLoader } from "react-spinners";

const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading } = useSelector((state: RootState) => state.auth);
  const email = location.state?.email;
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  if (!email) {
    toast.error("Missing email. Redirecting to signup.");
    setTimeout(() => navigate("/signup"), 2000);
    return null;
  }

  const handleChange = (index: number, value: string) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      setOtp(newOtp);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter a valid 6-digit code.");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      await dispatch(
        verifyOtp({
          type: "email",
          value: email,
          code,
        })
      ).unwrap();
      setIsVerifyingOtp(false);
      navigate("/signin");
    } catch (error: any) {
      setIsVerifyingOtp(false);
      return error;
    }
  };

  const handleResendCode = async () => {
    setIsResendingOtp(true);
    try {
      await dispatch(
        resendVerificationCode({
          type: "email",
          value: email,
        })
      ).unwrap();
      setIsResendingOtp(false);
    } catch (error: any) {
      setIsResendingOtp(false);
      return error;
    }
  };

  return (
    <AuthLayout {...authPageMeta.verify}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl p-8 shadow-xl border border-neutral-200 dark:border-neutral-700"
      >
        <h2 className="text-3xl font-bold text-center text-neutral-900 dark:text-white mb-2">
          Verify your email
        </h2>
        <p className="text-sm text-center text-neutral-600 dark:text-neutral-400 mb-6">
          We've sent a 6-digit code to{" "}
          <span className="font-medium text-neutral-900 dark:text-white">
            {email}
          </span>
        </p>

        <div className="flex justify-center gap-3 mb-6">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={loading || isResendingOtp}
              ref={(el) => (inputsRef.current[index] = el)}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-14 rounded-xl text-xl text-center border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition disabled:cursor-not-allowed disabled:opacity-50"
            />
          ))}
        </div>

        <ButtonPrimary
          onClick={handleVerify}
          disabled={loading || isVerifyingOtp}
          loading={isVerifyingOtp}
          className="w-full py-3 px-4 text-white text-sm font-semibold rounded-xl transition disabled:cursor-not-allowed disabled:text-gray-800 duration-300 hover:scale-105"
        >
          {!isVerifyingOtp && "Verify Email"}
        </ButtonPrimary>

        <div className="text-center mt-4">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            Didn’t receive a code?{" "}
            <button
              className="text-primary-500 dark:text-primary-400 font-medium hover:underline disabled:pointer-events-none disabled:opacity-50"
              onClick={handleResendCode}
              disabled={loading || isResendingOtp}
            >
              {isResendingOtp ? (
                <ClipLoader
                  loading={true}
                  size={14}
                  color="blue"
                  className="mx-4"
                />
              ) : (
                "Resend OTP"
              )}
            </button>
          </span>
        </div>

        <div className="text-center mt-4">
          <button
            onClick={() => navigate("/signin")}
            disabled={loading || isResendingOtp}
            className="text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300 disabled:pointer-events-none disabled:opacity-50"
          >
            Back to sign in
          </button>
        </div>
      </motion.div>
    </AuthLayout>
  );
};

export default VerifyEmail;
